/**
 * OwnerExecutiveBrief.js — COO-composed executive brief on top of Owner Shield Vault.
 * Provides: live ops counters, quick-action links (audit binder, live preview, refresh),
 * COO recommendations, and a direct Purchase Order dispatch panel.
 */
import React, { useEffect, useState } from "react";
import CooIdentityRibbon from "./CooIdentityRibbon";
import DoctrineBanner from "./DoctrineBanner";
import ProofOfEfficiencyCard from "./ProofOfEfficiencyCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";

const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(16px,3vw,26px)", marginBottom: 20 };
const btn = { background: CYAN, color: "#020A1F", border: 0, padding: "10px 18px", fontWeight: 700, borderRadius: 8, cursor: "pointer", fontSize: 12, letterSpacing: "0.03em" };
const btnGhost = { ...btn, background: "transparent", color: INK, border: `1px solid ${CHROME}55` };

export default function OwnerExecutiveBrief({ propertyAddress }) {
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(null);
  const [poStatus, setPoStatus] = useState(null);
  const [poOk, setPoOk] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!propertyAddress) return;
    fetch(`${BACKEND_URL}/api/role-dashboard/owner?property_address=${encodeURIComponent(propertyAddress)}`)
      .then(r => r.json()).then(setBrief).catch(e => setError(e.message));
    fetch(`${BACKEND_URL}/api/procurement/purchase-orders?property_address=${encodeURIComponent(propertyAddress)}&limit=10`)
      .then(r => r.json()).then(d => setPurchaseOrders(d.purchase_orders || []));
  }, [propertyAddress, refreshTick]);

  const issuePO = async () => {
    setPoStatus("Dispatching PO to Regional Trade Supply Co…");
    const r = await fetch(`${BACKEND_URL}/api/procurement/issue-po`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        property_address: propertyAddress,
        zone: "all",
        supplier_lane: "general_trade",
        dispatched_by: "owner_vault_ui",
      }),
    });
    const d = await r.json();
    if (!r.ok) {
      setPoStatus(`✗ ${d.detail || "PO issue failed"}`);
      return;
    }
    const po = d.purchase_order;
    const dispatched = po.webhook_status === "dispatched";
    setPoOk(dispatched);
    const badge = dispatched ? "✓" : "⚑";
    const line = dispatched
      ? `${badge} PO ${po.po_number} dispatched to ${po.supplier?.legal_name} · $${po.total_usd.toLocaleString()}`
      : `${badge} PO ${po.po_number} composed · $${po.total_usd.toLocaleString()} · supplier webhook: ${po.webhook_status} (retry/manual send required)`;
    setPoStatus(line);
    const listing = await fetch(`${BACKEND_URL}/api/procurement/purchase-orders?property_address=${encodeURIComponent(propertyAddress)}&limit=10`).then(x => x.json());
    setPurchaseOrders(listing.purchase_orders || []);
  };

  if (error) return <div style={{ ...card, borderColor: "#f04040" }}>Brief unavailable: {error}</div>;
  if (!brief) return null;

  const s = brief.executive_summary || {};

  return (
    <>
      <CooIdentityRibbon />
      <DoctrineBanner />
      <div style={{ margin: "24px 0" }}>
        <ProofOfEfficiencyCard />
      </div>
      <div style={{ ...card, borderColor: `${CYAN}66` }} data-testid="owner-executive-brief">
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
          COO-COMPOSED EXECUTIVE BRIEF · TIER-1 VAULT ADDENDUM
        </div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          {[
            ["Walkthroughs", s.walkthroughs_completed || 0],
            ["Field Captures", s.field_captures || 0],
            ["Line Items", s.line_items_procured || 0],
            ["Work Orders", s.work_orders_dispatched || 0],
          ].map(([label, val]) => (
            <div key={label} style={{ background: "rgba(0,0,0,0.25)", padding: 14, borderRadius: 8, border: `1px solid ${CHROME}22` }}>
              <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>{label.toUpperCase()}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: CYAN, marginTop: 4 }}>{val}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: CHROME, letterSpacing: "0.15em", marginBottom: 6 }}>COO RECOMMENDATIONS</div>
          {(brief.coo_recommendations || []).map((r, i) => (
            <div key={i} style={{ fontSize: 13, color: INK, padding: "4px 0" }} data-testid={`owner-rec-${i}`}>• {r}</div>
          ))}
        </div>

        <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {(brief.quick_actions || []).map((a, i) => {
            const isApi = a.url.startsWith("/api");
            const isRefresh = /refresh/i.test(a.label);
            if (isRefresh) {
              return (
                <button key={i} style={btnGhost} onClick={() => setRefreshTick(t => t + 1)}
                  data-testid={`owner-quick-action-${i}`}>
                  {a.label} →
                </button>
              );
            }
            const href = isApi ? `${BACKEND_URL}${a.url}` : a.url;
            return (
              <a key={i} href={href}
                target={isApi ? "_blank" : "_self"}
                rel="noreferrer" style={btnGhost} data-testid={`owner-quick-action-${i}`}>
                {a.label} →
              </a>
            );
          })}
          <button
            style={{
              ...btn,
              opacity: (s.line_items_procured || 0) === 0 ? 0.5 : 1,
              background: (s.line_items_procured || 0) === 0 ? "transparent" : CYAN,
              border: (s.line_items_procured || 0) === 0 ? `1px solid ${CHROME}55` : 0,
              color: (s.line_items_procured || 0) === 0 ? INK : "#020A1F",
              cursor: (s.line_items_procured || 0) === 0 ? "not-allowed" : "pointer",
            }}
            onClick={issuePO}
            disabled={(s.line_items_procured || 0) === 0}
            data-testid="owner-issue-po-btn">
            📦 Issue Purchase Order · Regional Trade Supply Co
          </button>
        </div>
        {(s.line_items_procured || 0) === 0 && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#f5a942" }} data-testid="owner-po-empty-warning">
            No materials on the ledger yet — add line items in Design Studio before issuing a PO.
          </div>
        )}

        {poStatus && <div style={{ marginTop: 10, color: poOk ? CYAN : "#f5a942", fontSize: 12 }} data-testid="owner-po-status">{poStatus}</div>}
      </div>

      {purchaseOrders.length > 0 && (
        <div style={card} data-testid="owner-po-ledger">
          <h3 style={{ marginTop: 0, color: CYAN, fontSize: 12, letterSpacing: "0.15em" }}>PURCHASE ORDER LEDGER · THIS ADDRESS</h3>
          {purchaseOrders.slice(0, 6).map(po => (
            <div key={po.po_number} style={{ padding: "10px 0", borderTop: `1px solid ${CHROME}22`, fontSize: 12 }}>
              <div><strong>{po.po_number}</strong> · {po.supplier?.legal_name || "unrouted"} · ${po.total_usd?.toLocaleString?.() ?? 0}</div>
              <div style={{ color: CHROME }}>{po.line_count} line(s) · zone: {po.zone} · webhook: {po.webhook_status} · status: {po.status}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
